import { Injectable } from '@angular/core';
import { getValueOrNull } from '@helpers/functions';
import { BehaviorSubject } from 'rxjs';
import { Contadores } from 'src/app/auth/interfaces/dadoslogin.interface';
import { AuthService } from 'src/app/auth/services/auth.service';
import { SideMenu } from 'src/app/models';
import { ApiProjetoService } from 'src/app/portal-coordenador/services/api-projeto.service';
import { links } from './links';
import { PortalService } from './portal.service';

@Injectable({ providedIn: 'root' })
export class SidebarLinksService {
    private readonly links = links;
    private readonly linksSubject = new BehaviorSubject<SideMenu[]>([]);
    readonly links$ = this.linksSubject.asObservable();

    constructor(
        private portalService: PortalService,
        private authService: AuthService,
        private apiProjetoCoordenadorService: ApiProjetoService
    ) {
        this.observarPortal();
        this.observarDadosLogin();
    }

    private observarPortal() {
        this.portalService.portal$.subscribe(this.atualizarLinks);
    }

    private observarDadosLogin() {
        this.authService.getDadosLogin().subscribe({
            next: dados => {
                const contadores = dados.contadores;
                this.updateAllBadges(contadores);
            },
            error: err => {
                console.error(err);
            },
        });
    }

    private atualizarLinks = (portal: string | undefined) => {
        this.linksSubject.next(this.links[portal] ?? []);
    };

    private updateAllBadges(contadores: Contadores) {
        this.updateBadgesColaborador(contadores);
        this.updateBadgesAdministrador(contadores);
        this.updateBadgesCoordenador(contadores);
        this.atualizarLinks(this.portalService.portal);
    }

    private updateBadgesColaborador(contadores: Contadores) {
        this.links['colaborador'] = this.links['colaborador'].map(menu => {
            if (
                menu.id === 'funcional' ||
                menu.id === 'requisicoes' ||
                menu.id === 'org-funcionamento'
            ) {
                let requer_atencao = false;

                const submenus = menu.submenus.map(submenu => {
                    if (
                        submenu.id === 'tarefas_atender' &&
                        menu.id === 'org-funcionamento'
                    ) {
                        const atenderSubmenu = submenu.submenus.find(
                            sub => sub.id === 'tarefas_atender'
                        );
                        if (atenderSubmenu) {
                            const status_key = atenderSubmenu.id
                                .split('-')
                                .join('_');
                            if (status_key in contadores) {
                                requer_atencao = contadores[status_key] > 0;
                                atenderSubmenu.badge = `<span class="badge badge-info">${getValueOrNull(
                                    contadores[status_key]
                                )}</span>`;
                            }
                        }
                        return { ...submenu, warn: requer_atencao };
                    }

                    const status_key = submenu.id.split('-').join('_');
                    if (status_key in contadores) {
                        requer_atencao = contadores[status_key] > 0;
                        submenu.badge = `<span class="badge badge-info">${getValueOrNull(
                            contadores[status_key]
                        )}</span>`;
                    }
                    return submenu;
                });

                menu = {
                    ...menu,
                    warn: requer_atencao,
                    submenus,
                };
                return menu;
            }

            return menu;
        });
    }

    private updateBadgesAdministrador(contadores: Contadores) {
        this.links['administrador'] = this.links['administrador'].map(menu => {
            if (
                menu.id === 'requisicoes' ||
                menu.id === 'suprimentos' ||
                menu.id === 'infraestrutura' ||
                menu.id === 'organizacao'
            ) {
                /** flag para ativar o badge de requisições para atender*/
                let requer_atencao = false;
                const submenus = menu.submenus.map(submenu => {
                    const status_key = submenu.id.split('-').join('_');
                    const status_key2 = submenu.id
                        .split('-')
                        .slice(1)
                        .join('_');
                    if (status_key in contadores) {
                        requer_atencao = contadores[status_key] > 0;

                        submenu.badge = `<span class="badge badge-info">${getValueOrNull(
                            contadores[status_key]
                        )}</span>`;
                    }

                    if (status_key2 in contadores) {
                        requer_atencao = contadores[status_key2] > 0;

                        submenu.warn = requer_atencao;
                        submenu.badge = `<span class="badge badge-info">${getValueOrNull(
                            contadores[status_key2]
                        )}</span>`;

                        let indexSubmenu = submenu.submenus.findIndex(
                            submenu => submenu.title === 'Processar'
                        );

                        submenu.submenus[
                            indexSubmenu
                        ].badge = `<span class="badge badge-info">${getValueOrNull(
                            contadores[status_key2]
                        )}</span>`;
                    }

                    return submenu;
                });

                menu = {
                    ...menu,
                    warn: requer_atencao,
                    submenus,
                };

                return menu;
            }
            return menu;
        });
    }

    private updateBadgesCoordenador(contadores: Contadores) {
        this.links['coordenador-original'] =
            this.links['coordenador-original'] ??
            JSON.parse(JSON.stringify(this.links['coordenador']));
        this.apiProjetoCoordenadorService.projeto$.subscribe(projeto => {
            if (!projeto) return;
            this.links['coordenador'] = this.links['coordenador-original'].map(
                menu => {
                    if (menu.id == 'suprimentos') {
                        let warn = false;
                        const submenus = menu.submenus.map(submenu => {
                            if (submenu.id == 'suprimentos-notas-fiscais') {
                                const badges = contadores.notas_projetos?.find(
                                    statusProjeto =>
                                        statusProjeto.titulo_curto ==
                                        projeto.titulo_curto
                                );
                                if (badges) {
                                    warn = badges.notas_fiscais > 0;
                                    submenu.badge = `<span class="badge badge-info">${badges.notas_fiscais}</span>`;
                                }
                            }
                            if (submenu.id === 'suprimentos-faturas') {
                                const badges = contadores.faturas_atestar?.find(
                                    ({ titulo_curto }) =>
                                        projeto?.titulo_curto === titulo_curto
                                );

                                if (badges) {
                                    warn = badges.faturas > 0;
                                    submenu.badge = `<span class="badge badge-info">${badges.faturas}</span>`;
                                }
                            }
                            return submenu;
                        });

                        return {
                            ...menu,
                            warn,
                            submenus,
                        };
                    }
                    if (menu.id == 'requisicoes') {
                        /** flag para ativar o badge de requisições caso existam requisições para atender */
                        let para_atender = false;

                        const submenus = menu.submenus.map(submenu => {
                            if (submenu.id == 'requisicoes-autorizar') {
                                para_atender =
                                    contadores['requisicoes_autorizar'][
                                        projeto.titulo_curto
                                    ] > 0;

                                submenu.badge = `<span class="badge badge-info">${
                                    contadores['requisicoes_autorizar'][
                                        projeto.titulo_curto
                                    ] || 0
                                }</span>`;
                            } else if (submenu.id in contadores) {
                                submenu.badge = `<span class="badge badge-info">${
                                    contadores[submenu.id]
                                }</span>`;
                            }
                            return submenu;
                        });

                        return {
                            ...menu,
                            warn: para_atender,
                            submenus,
                        };
                    }
                    return menu;
                }
            );
            this.atualizarLinks(this.portalService.portal);
        });
    }
}
